<template>
  <div id="blog-article" :default="$route.name" class="w-100 flex flex-column pt60">
    <div v-if="loading" class="fixed top-0 left-0 w-100 h-100 flex middle-xs center-xs z-1000" style="background: rgba(0, 0, 0, 0.5);">
      <div class="flex middle-xs cl-white" style="font-size: 24px; text-align: center;">
        <div class="spinner mr10" />
        <span>{{ $t('Loading...') }}</span>
      </div>
    </div>

    <div v-else-if="article" class="w-100">
      <!-- Breadcrumbs section -->
      <div class="bg-cl-secondary py35 pl20">
        <div class="container">
          <breadcrumbs :routes="breadcrumbs" :active-route="article.title" />
          <h1 class="fs-big mb20">
            {{ article.title }}
          </h1>
        </div>
      </div>

      <!-- Article content -->
      <div class="container mx-auto">
        <div class="row justify-content-center">
          <div class="col-md-10 col-lg-8 col-xs-12">
            <div class="static-content h4 lh30">
              <static-content
                v-if="article.pageContent"
                :id="article.id"
                :title="article.title"
                :page-content="article.pageContent"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="container py30">
      <p>{{ $t('Article not found') }}</p>
    </div>
  </div>
</template>

<script>
import { Logger } from '@vue-storefront/core/lib/logger'
import Breadcrumbs from 'theme/components/core/Breadcrumbs'
import StaticContent from 'theme/components/theme/StaticContent.vue'
import BLOG_ARTICLE_QUERY from '../../graphql/strapi/queries/blogArticle.gql'
import { getApolloProvider } from '@vue-storefront/core/scripts/resolvers/resolveGraphQL'

export default {
  name: 'BlogArticle',
  components: {
    Breadcrumbs,
    StaticContent
  },
  data () {
    return {
      loading: true,
      article: null,
      category: null
    }
  },
  computed: {
    pickupPoints () {
      return this.$store.getters['partner/getPickupPoints']
    },
    breadcrumbs () {
      const baseBreadcrumbs = [
        {
          name: this.$t('Home'),
          route_link: '/'
        },
        {
          name: this.$t('Blog'),
          route_link: '/blog'
        }
      ]

      if (this.category) {
        baseBreadcrumbs.push({
          name: this.category.name,
          route_link: `/blog/${this.category.slug}`
        })
      }

      return baseBreadcrumbs
    }
  },
  methods: {
    async fetchArticleData (slug, apolloClient) {
      try {
        Logger.info('Fetching blog article data for slug:', slug)
        const response = await apolloClient.query({
          query: BLOG_ARTICLE_QUERY,
          variables: {
            filters: {
              slug: { eq: slug }
            }
          }
        })

        const articleData = response.data.blogArticles.data[0]
        console.log('Article data from API:', articleData) // Debug log

        if (articleData) {
          const article = {
            id: articleData.id,
            title: articleData.attributes.Title,
            metaDescription: articleData.attributes.MetaDescription,
            slug: articleData.attributes.slug,
            pageContent: articleData.attributes.PageContent || [] // Ensure this is an array
          }

          // Extract category information if available
          let category = null
          if (articleData.attributes.categories && articleData.attributes.categories.data.length > 0) {
            const categoryData = articleData.attributes.categories.data[0]
            category = {
              name: categoryData.attributes.name,
              slug: categoryData.attributes.slug
            }
          }

          console.log('Processed article data:', article) // Debug log
          console.log('Category data:', category) // Debug log

          // Update component data
          this.article = article
          this.category = category
          this.loading = false

          // Return the article data for serverPrefetch
          return { article, category, loading: false }
        }

        this.loading = false
        return { article: null, category: null, loading: false }
      } catch (error) {
        Logger.error('Cannot load blog article data: ' + error)
        this.loading = false
        return { article: null, category: null, loading: false }
      }
    }
  },
  mounted () {
    // Only fetch on client if we don't have data from SSR
    if (!this.article) {
      const slug = this.$route.params.articleSlug
      console.log('Fetching blog article data on client side for slug:', slug)
      this.fetchArticleData(slug, this.$apollo)
    }
  },
  // This is the key method for SSR data fetching
  async asyncData ({ context, route }) {
    if (context) context.output.cacheTags.add(`blog`)
    Logger.info('Calling asyncData in BlogArticle')()
    const slug = route.params.articleSlug

    try {
      // Get Apollo client for server-side
      const apolloProvider = await getApolloProvider()
      const apolloClient = apolloProvider.defaultClient

      const response = await apolloClient.query({
        query: BLOG_ARTICLE_QUERY,
        variables: {
          filters: {
            slug: { eq: slug }
          }
        }
      })

      const articleData = response.data.blogArticles.data[0]

      if (articleData) {
        // Extract category information if available
        let category = null
        if (articleData.attributes.categories && articleData.attributes.categories.data.length > 0) {
          const categoryData = articleData.attributes.categories.data[0]
          category = {
            name: categoryData.attributes.name,
            slug: categoryData.attributes.slug
          }
        }

        // Return data to be merged with component data
        return {
          article: {
            id: articleData.id,
            title: articleData.attributes.Title,
            metaDescription: articleData.attributes.MetaDescription,
            slug: articleData.attributes.slug,
            pageContent: articleData.attributes.PageContent
          },
          category,
          loading: false
        }
      }

      return { article: null, category: null, loading: false }
    } catch (error) {
      Logger.error('Error in asyncData for BlogArticle: ' + error)()
      return { article: null, category: null, loading: false }
    }
  },
  serverPrefetch () {
    Logger.info('Server-side prefetching blog article')()
    const slug = this.$route.params.articleSlug

    // Return a promise that resolves with the data
    return this.fetchArticleData(slug, this.$apollo).then(data => {
      // This is crucial - we need to return the data to properly hydrate the component
      return data
    })
  },
  metaInfo () {
    return {
      title: this.article ? this.article.title : this.$t('Blog Article'),
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.article && this.article.metaDescription
            ? this.article.metaDescription
            : this.$t('Blog Article')
        }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/helpers/functions/color';
@import '~theme/css/variables/colors';

.pickup-point-viewer-container {
  width: 100%;
  height: 500px;
  padding-bottom: 50px;
}
</style>
